import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { copyReferralLink, ReferralInfo, REFERRAL_SHARE_SHEET_EVENT } from "../utils/referral";
import "./styles.css";

const EMAIL_SUBJECT = "Join me on U4U!";

const CopyIcon = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V6a2 2 0 0 1 2-2h8" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width="30" height="30">
    <path fill="none" stroke="#ffffff" strokeWidth="1.6" strokeLinejoin="round" d="M12 4.2a7.8 7.8 0 0 0-6.7 11.8L4.2 19.8l3.9-1.1A7.8 7.8 0 1 0 12 4.2z" />
    <path fill="#ffffff" d="M9.3 8.4c.2-.4.4-.4.7-.4h.5c.2 0 .4 0 .5.4l.7 1.6c.1.2.1.4 0 .5l-.4.6c-.1.2-.1.3 0 .5.5.9 1.2 1.6 2.1 2.1.2.1.3.1.5 0l.6-.4c.2-.1.3-.1.5 0l1.6.7c.4.2.4.3.4.5v.5c0 .3 0 .5-.4.7-.5.3-1.6.6-3.4-.5-1.6-1-2.8-2.2-3.7-3.8-.9-1.6-.5-2.7-.2-3z" />
  </svg>
);

const GmailIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width="30" height="30" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path stroke="#4285F4" d="M5 8v9" />
    <path stroke="#34A853" d="M19 8v9" />
    <path stroke="#EA4335" d="M5 8l7 5.2L19 8" />
  </svg>
);

const EmailIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="6" width="16" height="12" rx="2" />
    <path d="M4.5 7l7.5 6 7.5-6" />
  </svg>
);

const FacebookIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" width="30" height="30">
    <path fill="#ffffff" d="M13.4 20v-6.6h2.2l.4-2.6h-2.6V9.2c0-.8.2-1.3 1.3-1.3H16V5.6c-.2 0-1-.1-2-.1-2 0-3.4 1.2-3.4 3.5v1.8H8.4v2.6h2.2V20h2.8z" />
  </svg>
);

type ShareTarget = {
  name: string;
  className: string;
  icon: React.ReactNode;
  getUrl: (text: string, link: string) => string;
};

const SHARE_TARGETS: ShareTarget[] = [
  {
    name: "WhatsApp",
    className: "referral-share-icon-whatsapp",
    icon: <WhatsAppIcon />,
    getUrl: (text) => `https://wa.me/?text=${encodeURIComponent(text)}`,
  },
  {
    name: "Gmail",
    className: "referral-share-icon-gmail",
    icon: <GmailIcon />,
    getUrl: (text) =>
      `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(text)}`,
  },
  {
    name: "Email",
    className: "referral-share-icon-email",
    icon: <EmailIcon />,
    getUrl: (text) => `mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(text)}`,
  },
  {
    name: "Facebook",
    className: "referral-share-icon-facebook",
    icon: <FacebookIcon />,
    // Facebook only takes the link, it ignores any prefilled text
    getUrl: (_text, link) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
  },
];

// Share sheet for browsers without navigator.share. Mounted once in App and
// opened by shareReferral through REFERRAL_SHARE_SHEET_EVENT.
export const ReferralShareSheet: React.FC = () => {
  const [info, setInfo] = useState<ReferralInfo | null>(null);

  useEffect(() => {
    const onOpen = (event: Event) => setInfo((event as CustomEvent<ReferralInfo>).detail);
    window.addEventListener(REFERRAL_SHARE_SHEET_EVENT, onOpen);
    return () => window.removeEventListener(REFERRAL_SHARE_SHEET_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!info) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setInfo(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [info]);

  if (!info) return null;

  const close = () => setInfo(null);
  const shareText = `${info.shareMessage} ${info.shareLink}`;

  const onTarget = (target: ShareTarget) => {
    const url = target.getUrl(shareText, info.shareLink);
    if (url.startsWith("mailto:")) {
      window.location.href = url;
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    close();
  };

  const modalRoot = document.getElementById("modal-root") || document.body;
  return createPortal(
    <div className="referral-share-overlay" onClick={close}>
      <div
        className="referral-share-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Share Link"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="referral-share-handle" />
        <h2 className="referral-share-title">Share Link</h2>
        <div className="referral-share-link">
          <span className="referral-share-link-text">{info.shareLink}</span>
          <button
            type="button"
            className="referral-share-copy"
            aria-label="Copy link"
            onClick={() => copyReferralLink(info)}
          >
            <CopyIcon />
          </button>
        </div>
        <div className="referral-share-targets">
          {SHARE_TARGETS.map((target) => (
            <button
              key={target.name}
              type="button"
              className="referral-share-target"
              onClick={() => onTarget(target)}
            >
              <span className={`referral-share-icon ${target.className}`}>{target.icon}</span>
              <span className="referral-share-target-name">{target.name}</span>
            </button>
          ))}
        </div>
        <button type="button" className="referral-share-cancel" onClick={close}>
          Cancel
        </button>
      </div>
    </div>,
    modalRoot
  );
};
